import { useRef, useState } from "react";
import { Box, Textarea, SimpleGrid, Text, Flex, MultiSelect, Group, Stack, TextInput, Button, Badge } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import {
  getSubIndustriesByIndustry,
  getCollaborationIntentOptions,
  getWhoAmIOptions,
  getWhoAmIDescription,
} from "@/components/shared/constants/general.const";

interface BusinessOverviewProps {
  formData: {
    companyDescription: string;
    subDescription: string;
    operations: string[];
    industryCategory: string[];
    subIndustry: string[];
    whoAmI: string[];
    collaborationIntent: string[];
  };
  updateForm: (field: string, value: any) => void;
  industryOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export default function BusinessOverview({
  formData,
  updateForm,
  industryOptions,
  disabled,
}: BusinessOverviewProps) {
  const [whoAmIHoverValue, setWhoAmIHoverValue] = useState<string | null>(null);
  const whoAmIFieldRef = useRef<HTMLDivElement | null>(null);
  const [whoAmIDropdownPosition, setWhoAmIDropdownPosition] = useState<"bottom-start" | "top-start">("bottom-start");
  const [operationsInput, setOperationsInput] = useState("");
  const [operationsError, setOperationsError] = useState<string | null>(null);
  const WHO_AM_I_MIN_SPACE_BELOW = 360;
  const MAX_OPERATIONS = 10;

  // Get sub-industry options based on all selected industries (merged + deduplicated)
  const subIndustryOptions =
    formData.industryCategory.length > 0
      ? formData.industryCategory
          .flatMap((ind) => getSubIndustriesByIndustry(ind))
          .filter((opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i)
      : [];

  // Clear sub-industry when industry changes
  const handleIndustryChange = (values: string[]) => {
    updateForm("industryCategory", values);
    updateForm("subIndustry", []); // Reset sub-industries when industry changes
  };

  const handleAddOperations = () => {
    if ((formData.operations || []).length >= MAX_OPERATIONS) {
      setOperationsError(`Maximum ${MAX_OPERATIONS} operations allowed`);
      return;
    }
    if (!operationsInput.trim()) return;

    const values = operationsInput
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v);
    const existing = formData.operations || [];
    const remainingSlots = MAX_OPERATIONS - existing.length;

    if (values.length > remainingSlots) {
      setOperationsError(`Only ${remainingSlots} more operations can be added`);
      return;
    }

    const uniqueNewValues = values.filter((v) => !existing.includes(v));
    updateForm("operations", [...existing, ...uniqueNewValues]);
    setOperationsInput("");
    setOperationsError(null);
  };

  const handleRemoveOperations = (index: number) => {
    const values = [...(formData.operations || [])];
    values.splice(index, 1);
    updateForm("operations", values);
    setOperationsError(null);
  };

  return (
    <Box>
      {/* Company Description */}
      <Box mb="md">
        <Text fz="sm" fw={500} mb={4}>
          Company Description
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        </Text>
        <Textarea
          placeholder="Describe your company, what you do, your expertise, and what makes you unique..."
          autosize
          minRows={2}
          maxLength={800}
          value={formData.companyDescription}
          onChange={(e) => updateForm("companyDescription", e.currentTarget.value)}
          disabled={disabled}
          error={formData.companyDescription.length > 800 ? "Description cannot exceed 800 characters" : null}
        />
        <Flex justify="space-between" mt={4}>
          <Text fz="xs" c="slate.6">
            Describe your company, expertise, and what makes you unique.
          </Text>
          <Text fz="xs" c="orange">
            {formData.companyDescription.length} / 800 characters
          </Text>
        </Flex>
      </Box>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        <TextInput
          label="Sub Description (Optional)"
          placeholder="One short line describing what your company does"
          maxLength={180}
          value={formData.subDescription || ""}
          onChange={(e) => updateForm("subDescription", e.currentTarget.value)}
          disabled={disabled}
        />
        <Box>
          <Text fz="sm" fw={500} mb={4}>
            Operations (Optional){" "}
            <Text span c="slate.6">
              ({(formData.operations || []).length} / {MAX_OPERATIONS})
            </Text>
          </Text>
          <Text fz="xs" c={operationsError ? "red" : "slate.6"} mb="xs">
            {operationsError || "Add up to 10 operations. Press Enter or use comma-separated values"}
          </Text>
          <Flex gap="sm" mb="xs">
            <TextInput
              placeholder="eg: manufacturing, design, distribution"
              value={operationsInput}
              onChange={(e) => {
                setOperationsInput(e.currentTarget.value);
                setOperationsError(null);
              }}
              style={{ flex: 1 }}
              disabled={disabled}
              error={Boolean(operationsError)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddOperations();
                }
              }}
            />
            <Button variant="outline" size="sm" disabled={disabled} onClick={handleAddOperations}>
              Add
            </Button>
          </Flex>
          <Flex gap="xs" wrap="wrap">
            {(formData.operations || []).map((op, index) => (
              <Badge
                key={`${op}-${index}`}
                variant="light"
                rightSection={
                  !disabled && (
                    <IconX size={12} style={{ cursor: "pointer" }} onClick={() => handleRemoveOperations(index)} />
                  )
                }
              >
                {op}
              </Badge>
            ))}
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Industry Category & Sub-industry */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Industry Category
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          placeholder="Select industries"
          data={industryOptions}
          value={formData.industryCategory || []}
          onChange={handleIndustryChange}
          searchable
          disabled={disabled}
          clearable
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Sub-industry (Optional)
            </Text>
          }
          placeholder={formData.industryCategory.length > 0 ? "Select sub-industries" : "Select industry first"}
          data={subIndustryOptions}
          value={formData.subIndustry || []}
          onChange={(value) => updateForm("subIndustry", value)}
          searchable
          disabled={disabled || formData.industryCategory.length === 0}
          clearable
          maxValues={5}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <Box ref={whoAmIFieldRef}>
          <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Who am I?
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          description="Hover a role in the list to see a short explanation (inside the dropdown)"
          placeholder="Select your role"
          data={getWhoAmIOptions()}
          value={formData.whoAmI || []}
          onChange={(value) => updateForm("whoAmI", value)}
          searchable
          disabled={disabled}
          clearable
          withScrollArea
          maxDropdownHeight={320}
          comboboxProps={{
            withinPortal: true,
            position: whoAmIDropdownPosition,
            middlewares: { flip: false, shift: false },
          }}
          onDropdownOpen={() => {
            const fieldRect = whoAmIFieldRef.current?.getBoundingClientRect();
            if (!fieldRect) return;
            const availableSpaceBelow = window.innerHeight - fieldRect.bottom;
            setWhoAmIDropdownPosition(availableSpaceBelow < WHO_AM_I_MIN_SPACE_BELOW ? "top-start" : "bottom-start");
          }}
          onDropdownClose={() => setWhoAmIHoverValue(null)}
          renderOption={({ option, checked }) => {
            const detail = getWhoAmIDescription(option.value);
            const showDetail = detail && whoAmIHoverValue === option.value;
            return (
              <Box
                w="100%"
                onMouseEnter={() => setWhoAmIHoverValue(option.value)}
                onMouseLeave={() => setWhoAmIHoverValue((prev) => (prev === option.value ? null : prev))}
              >
                <Group gap={10} wrap="nowrap" align="flex-start" w="100%" py={showDetail ? 8 : 4}>
                  <Box w={20} miw={20} style={{ display: "flex", justifyContent: "center", flexShrink: 0, paddingTop: 2 }}>
                    {checked ? <IconCheck size={14} stroke={2.5} style={{ opacity: 0.85 }} /> : null}
                  </Box>
                  <Stack gap={showDetail ? 6 : 0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} lh={1.35}>
                      {option.label}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      lh={1.5}
                      style={{
                        whiteSpace: "normal",
                        overflow: "hidden",
                        maxHeight: showDetail ? 72 : 0,
                        opacity: showDetail ? 1 : 0,
                        marginTop: showDetail ? 2 : 0,
                        transition: "max-height 240ms ease, opacity 240ms ease, margin-top 240ms ease",
                      }}
                    >
                      {detail}
                    </Text>
                  </Stack>
                </Group>
              </Box>
            );
          }}
        />
        </Box>
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Collaboration Intent (Optional)
            </Text>
          }
          description="Select the types of collaboration you're looking for"
          placeholder="Select collaboration types"
          data={getCollaborationIntentOptions()}
          value={formData.collaborationIntent || []}
          onChange={(value) => updateForm("collaborationIntent", value)}
          searchable
          disabled={disabled}
          clearable
          maxValues={5}
        />
      </SimpleGrid>
    </Box>
  );
}
